
import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent } from "./ui/tabs";
import { Building2, Users, AlertCircle, AlertTriangle, Brain, CheckCircle, ChevronDown, ChevronUp, Copy, Eye, EyeOff, Lightbulb, Share2, Star, Target, ThumbsDown, ThumbsUp, TrendingUp, ArrowRight, Sparkles, Clock, Bookmark, Upload, ChartBar } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import apiService from "../services/apiService";
import { FinancialDataPeerComparison } from '../types/financial';
import YearComparisonTable from './YearComparisonTable';
import PeerTable from './PeerTable';
import ErrorBoundary from './ErrorBoundary';
import { Button } from "./ui/button";

type FinancialDatapops = {
    financialData?: FinancialDataPeerComparison,
    customerId?: string
};
interface ApiResponse<T> {
    data: T;
    likeBtn?: number
    actionButton?: string;
    company?: any;
    gstEpfo?: {
        gst_details: Array<any>;
        establishments_registered_with_epfo: Array<any>;
    };
    message?: string;
    status?: number;
    // financial?:FinancialTabProps
}


const PeerComparison: React.FC<FinancialDatapops> = ({ financialData, customerId }) => {
    const [selectedSegment, setSelectedSegment] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [feedback, setFeedback] = useState<1 | 0 | null>(null);
    // Real JSON data structure - this would come from API in production
    const [isOpen, setIsOpen] = useState(false);
    const [data, SetData] = useState<any>({})
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (selectedSegment != '')
            getChatGptResponse();
    }, [selectedSegment])

    const getChatGptResponse = async () => {
        const [response] = await Promise.all([
            apiService.get<ApiResponse<any>>(`/customers/financials/get-peer-respose/${customerId}`, { params: { selectedSegment } })
        ]);
        SetData(response.data);
        setFeedback(
            response.likeBtn === 0 || response.likeBtn === 1
                ? response.likeBtn
                : null
        );
    }

    // Computed values using proper JSON structure
    useEffect(() => {
        if (
            financialData?.peer_comparison &&
            financialData.peer_comparison.length > 0 &&
            !selectedSegment // Only if not already selected
        ) {
            setSelectedSegment(financialData.peer_comparison[0].bizSegment);
        }
    }, [financialData, selectedSegment]);

    const currentData = useMemo(() => {
        return financialData?.peer_comparison.find(
            segment => segment.bizSegment === selectedSegment
        );
    }, [selectedSegment, financialData]);

    const availableSegments = useMemo(() => {
        return financialData?.peer_comparison.map(segment => ({
            value: segment.bizSegment,
            label: `${segment.bizIndustry} | ${segment.bizSegment}`
        }));
    }, []);



    const handleSegmentChange = async (value: string) => {
        setIsLoading(true);
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));
        setSelectedSegment(value);
        setIsLoading(false);
    };

    const handleFeedback = async (type: 1 | 0) => {

        try {
            const response: any = await apiService.post("customers/financials/update-gpt-like", {
                customerId,
                type,
                selectedSegment
            });
            setFeedback(type);
        } catch (e: any) {

        }

    };

    let displayRisks = data?.key_credit_risks || [];
    let displayStrengths = data?.key_strengths || [];
    let displayRecommendations = data.underwriting_insights_and_decision_signal || [];

    if (!currentData) {
        return (
            <div className="bg-white flex items-center justify-center">
                <Card className="border-red-200 bg-red-50">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-red-700">
                            <AlertCircle className="w-5 h-5" />
                            Data Not Available
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-red-600">
                            No data found for the selected segment. Please try refreshing the page or contact support.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }




    const handleGenerate = async () => {
        try {
            setLoading(true);
            const [response] = await Promise.all([
                apiService.get<ApiResponse<any>>(`/customers/financials/generatePeerGpt/${customerId}`, { params: { selectedSegment } })
            ]);
        } catch {
            setLoading(false);
        } finally {
            setLoading(false);
        }

        getChatGptResponse();
    };



    return (
        <ErrorBoundary>
            <div className="bg-white">
                <div className="max-w-7xl mx-auto space-y-8">

                    {/* Segment Selector */}
                    <Card className="w-full shadow-lg border border-[#d9cba6]/30 hover:shadow-xl transition-shadow duration-300">
                        <CardHeader className="bg-gradient-to-r from-[#d9cba6]/10 to-[#d9cba6]/5 p-[1px]">
                            <CardTitle className="flex items-center gap-2 text-[#8b7355] p-3">
                                <Building2 className="w-6 h-6 text-[#c4965f]" />
                                Select Business Segment
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3">
                            <Select value={selectedSegment} onValueChange={handleSegmentChange}>
                                <SelectTrigger className="w-full border-[#d9cba6]/40 focus:border-[#c4965f] focus:ring-[#d9cba6]/20">
                                    <SelectValue placeholder="Choose a business segment" />
                                </SelectTrigger>
                                <SelectContent className="border-[#d9cba6]/30">
                                    {availableSegments?.map(segment => (
                                        <SelectItem key={segment.value} value={segment.value} className="hover:bg-[#d9cba6]/10">
                                            {segment.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                        </CardContent>
                    </Card>

                    {!data?.financial_position ? (
                        <div className="flex justify-end gap-3 animate-fade-in">

                            <Button
                                disabled={loading}
                                variant="outline"
                                className="flex items-center gap-2 border-purple-300 text-purple-700 hover:bg-purple-100 
                  hover:scale-105 transition-all duration-300 shadow-sm hover:shadow-md"
                                onClick={handleGenerate}
                            >
                                {loading && (<div className="h-5 w-5 border-2 border-[#9E7926] border-t-transparent rounded-full animate-spin" />)}
                                <Brain className="h-4 w-4 transition-transform group-hover:scale-110" />
                                <span> AFIN Overview </span>
                            </Button>

                        </div>
                    ) : (
                        <div className="relative">
                            {/* Modern AI-styled container */}
                            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-lg overflow-hidden">
                                {/* Subtle gradient overlay */}
                                <div className="absolute inset-0 bg-gradient-to-br from-[#9E7926]/3 via-transparent to-[#489BA8]/3 pointer-events-none" />

                                {/* Header Section */}
                                <div className="relative p-6 border-b border-gray-100/50">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div className="relative">
                                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#9E7926] to-[#489BA8] p-0.5">
                                                    <div className="w-full h-full rounded-xl bg-white/95 flex items-center justify-center">
                                                        <Brain className="w-6 h-6 text-[#9E7926]" />
                                                    </div>
                                                </div>
                                                <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-[#489BA8] to-[#9E7926] rounded-full flex items-center justify-center">
                                                    <Sparkles className="w-3 h-3 text-white" />
                                                </div>
                                            </div>

                                            <div>
                                                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                                    AFIN Overview

                                                </h2>
                                                <p className="text-sm text-gray-600 mt-1">
                                                    AI-Powered Underwriting Intelligence
                                                </p>
                                            </div>
                                        </div>


                                    </div>


                                </div>

                                {/* Content Section */}
                                <div className="relative p-6 space-y-8">
                                    {/* Executive Summary */}
                                    <section className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#9E7926]/20 to-[#489BA8]/20 flex items-center justify-center">
                                                    <Lightbulb className="w-4 h-4 text-[#9E7926]" />
                                                </div>
                                                <h3 className="text-lg font-semibold text-gray-900">Executive Summary</h3>
                                            </div>
                                        </div>

                                        <div className="relative">
                                            <div className="relative p-6 rounded-xl bg-gradient-to-br from-[#9E7926]/5 to-[#489BA8]/5 border border-[#9E7926]/10">
                                                <p className="text-gray-700 leading-relaxed">
                                                    {data.financial_position}
                                                </p>
                                            </div>
                                        </div>
                                    </section>

                                    {/* Collapsible Detailed Analysis */}
                                    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                                        <CollapsibleTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-full justify-center gap-3 h-11 rounded-xl border-2 border-gray-200 hover:border-[#9E7926]/30 hover:bg-gray-50/50 transition-all duration-200"
                                            >
                                                {isOpen ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                <span className="font-medium">{isOpen ? 'Hide' : 'Show'} Detailed Analysis</span>
                                                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                            </Button>
                                        </CollapsibleTrigger>

                                        <CollapsibleContent className="space-y-8 mt-6">
                                            {/* Key Risks */}
                                            <section className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                                                            <AlertTriangle className="w-4 h-4 text-red-500" />
                                                        </div>
                                                        <h4 className="text-lg font-semibold text-gray-900">Key Credit Risks</h4>
                                                    </div>


                                                </div>

                                                <div className="space-y-3">
                                                    {displayRisks.map((risk, index) => (
                                                        <div key={index} className="flex items-start space-x-3 group">
                                                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-50 flex items-center justify-center text-red-500 text-sm font-medium mt-0.5">
                                                                {index + 1}
                                                            </div>
                                                            <p className="text-gray-700 leading-relaxed flex-1 pt-0.5">{risk}</p>
                                                        </div>
                                                    ))}
                                                </div>


                                            </section>

                                            {/* Key Strengths */}
                                            <section className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                                                            <TrendingUp className="w-4 h-4 text-green-500" />
                                                        </div>
                                                        <h4 className="text-lg font-semibold text-gray-900">Key Strengths</h4>
                                                    </div>

                                                </div>

                                                <div className="space-y-3">
                                                    {displayStrengths.map((strength, index) => (
                                                        <div key={index} className="flex items-start space-x-3 group">
                                                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-50 flex items-center justify-center mt-0.5">
                                                                <Star className="w-4 h-4 text-green-500" />
                                                            </div>
                                                            <p className="text-gray-700 leading-relaxed flex-1 pt-0.5">{strength}</p>
                                                        </div>
                                                    ))}
                                                </div>


                                            </section>

                                            {/* Recommendations */}
                                            <section className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                                                            <Target className="w-4 h-4 text-blue-500" />
                                                        </div>
                                                        <h4 className="text-lg font-semibold text-gray-900">Recommendations</h4>
                                                    </div>


                                                </div>

                                                <div className="space-y-3">
                                                    {displayRecommendations.map((rec, index) => (
                                                        <div key={index} className="flex items-start space-x-3 group">
                                                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center mt-0.5">
                                                                <ArrowRight className="w-4 h-4 text-blue-500" />
                                                            </div>
                                                            <p className="text-gray-700 leading-relaxed flex-1 pt-0.5">{rec}</p>
                                                        </div>
                                                    ))}
                                                </div>


                                            </section>
                                        </CollapsibleContent>
                                    </Collapsible>
                                </div>

                                {/* Feedback Section */}
                                <div className="relative px-6 py-4 border-t border-gray-100/50 bg-gray-50/30">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <p className="text-sm text-gray-600 font-medium">Was this analysis helpful?</p>

                                            <div className="flex space-x-2">
                                                <Button
                                                    variant={feedback === 1 ? 'default' : 'outline'}
                                                    size="sm"
                                                    onClick={() => handleFeedback(1)}
                                                    className={`h-8 px-3 rounded-full text-xs ${feedback === 1
                                                        ? 'bg-green-500 hover:bg-green-600 text-white'
                                                        : 'border-gray-200 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    <ThumbsUp className="w-3 h-3 mr-1" />
                                                    Yes
                                                </Button>

                                                <Button
                                                    variant={feedback === 0 ? 'destructive' : 'outline'}
                                                    size="sm"
                                                    onClick={() => handleFeedback(0)}
                                                    className={`h-8 px-3 rounded-full text-xs ${feedback === 0
                                                        ? 'bg-red-500 hover:bg-red-600 text-white'
                                                        : 'border-gray-200 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    <ThumbsDown className="w-3 h-3 mr-1" />
                                                    No
                                                </Button>
                                            </div>
                                        </div>

                                        {feedback !== null && feedback !== undefined && (
                                            <p className="text-sm text-green-600 font-medium">
                                                Thank you for your feedback! ✨
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}


                    <Tabs defaultValue="metrics" className="w-full">

                        <TabsContent value="metrics" className="space-y-8 mt-8">
                            {/* Year-over-Year Financial Metrics Comparison Table */}
                            <Card className="shadow-lg border border-[#d9cba6]/30">
                                <CardHeader className="bg-gradient-to-r from-[#d9cba6]/10 to-[#d9cba6]/5 p-3">
                                    <CardTitle className="text-[#8b7355] text-xl">Key Financial Metrics: Company vs. Segment Median (Year-over-Year)</CardTitle>

                                </CardHeader>
                                <CardContent className="p-3">
                                    <YearComparisonTable benchmarks={currentData.benchMarks} isLoading={isLoading} />
                                </CardContent>
                            </Card>

                            <Card className="shadow-lg border border-[#d9cba6]/30">
                                <CardHeader className="bg-gradient-to-r from-[#d9cba6]/10 to-[#d9cba6]/5">
                                    <CardTitle className="flex items-center gap-2 text-[#8b7355]">
                                        <Users className="w-6 h-6 text-[#c4965f]" />
                                        {currentData.peers.length} Closest Peers by Revenue (FY {currentData.refYear})
                                    </CardTitle>

                                </CardHeader>
                                <CardContent className="p-0">
                                    <PeerTable peers={currentData.peers} isLoading={isLoading} />
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
        </ErrorBoundary>
    );
};

export default PeerComparison;
